/**
 * UsageBanner - Subtle banner showing usage approaching limit
 * Shows when user is at 50%+ of their limit with upgrade link
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, Sparkles, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { billingApi, featuresApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';

const UsageBanner = ({ 
  limitType = 'products',  // 'products' or 'features'
  className,
  showAtPercentage = 50,  // Show banner when usage >= this % of limit
  dismissible = true,
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        if (limitType === 'products') {
          const response = await billingApi.getLimits();
          const data = response.data;
          setUsage({
            used: data.products?.used || 0,
            limit: data.products?.limit,
            remaining: data.products?.remaining,
          });
        } else if (limitType === 'features') {
          // Use the features API for feature limits
          const response = await featuresApi.getUsage();
          const data = response.data;
          setUsage({
            used: data.used || 0,
            limit: data.limit,
            remaining: data.remaining,
          });
        }
      } catch (error) {
        console.error('Failed to fetch usage:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsage();
  }, [limitType]);
  
  // Don't show if loading, dismissed, or no limit
  if (loading || dismissed || !usage?.limit) {
    return null;
  }
  
  const percentage = (usage.used / usage.limit) * 100;
  
  // Don't show if below threshold
  if (percentage < showAtPercentage) {
    return null;
  }
  
  // Determine banner style based on usage
  const isAtLimit = usage.used >= usage.limit;
  const isNearLimit = percentage >= 80;
  
  const getBannerStyle = () => {
    if (isAtLimit) {
      return {
        bg: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
        border: 'border-amber-200 dark:border-amber-700',
        icon: 'text-amber-600 dark:text-amber-400',
        text: 'text-amber-800 dark:text-amber-200',
        link: 'text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100',
      };
    }
    if (isNearLimit) {
      return {
        bg: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
        text: 'text-blue-800 dark:text-blue-200',
        link: 'text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100',
      };
    }
    return {
      bg: 'bg-gray-50 dark:bg-gray-800/50',
      border: 'border-gray-200 dark:border-gray-700',
      icon: 'text-gray-500 dark:text-gray-400',
      text: 'text-gray-600 dark:text-gray-300',
      link: 'text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100',
    };
  };
  
  const style = getBannerStyle();
  const limitLabel = limitType === 'products' ? 'products' : 'features';
  
  const getMessage = () => {
    if (isAtLimit) {
      return `You've reached your ${limitLabel} limit.`;
    }
    if (usage.remaining === 1) {
      return `You have 1 ${limitType === 'products' ? 'product' : 'feature'} slot remaining.`;
    }
    return `You're using ${usage.used} of ${usage.limit} ${limitLabel}.`;
  };
  
  return (
    <div className={cn(
      "flex items-center justify-between px-4 py-2.5 rounded-lg border",
      style.bg,
      style.border,
      className
    )}>
      <div className="flex items-center gap-3">
        {isAtLimit ? (
          <AlertCircle className={cn("w-4 h-4 flex-shrink-0", style.icon)} />
        ) : (
          <Sparkles className={cn("w-4 h-4 flex-shrink-0", style.icon)} />
        )}
        
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn("text-sm font-medium", style.text)}>
            {getMessage()}
          </span>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-1.5">
            <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full rounded-full transition-all",
                  isAtLimit ? "bg-amber-500" : isNearLimit ? "bg-blue-500" : "bg-gray-400"
                )}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
            <span className={cn("text-xs", style.text)}>
              {usage.used}/{usage.limit}
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Link 
          to="/settings?tab=billing"
          className={cn(
            "flex items-center gap-1 text-sm font-medium transition-colors",
            style.link
          )}
        >
          {isAtLimit ? 'Upgrade now' : 'Upgrade'}
          <ArrowRight className="w-3 h-3" />
        </Link>
        
        {dismissible && !isAtLimit && (
          <button
            onClick={() => setDismissed(true)}
            className={cn(
              "p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors",
              style.icon
            )}
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default UsageBanner;
